import { Box, Flex } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useState, useRef } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { AlarmClockIcon, PaperclipIcon, PromoTagIcon, StopIcon } from 'Asset/icon';
import HeaderMobile from 'Component/header-mobile';
import { TextMedium, TextRegular } from 'UiKit/typography';
import ButtonOutline from 'UiKit/button/buttonOutline';
import ButtonSolid from 'UiKit/button/buttonSolid';
import ScrollToTopUiKit from 'UiKit/scroll-to-top';
import { useUserInfo } from 'Store/userInfo';

import BriefLiveWidget from 'View/host-stream/components/brief-live-widget';
import HelpHostWidget from 'View/host-stream/components/help-host-widget';
import LivestreamHeader from 'View/host-stream/components/livestream-banner';
import useIsHelpHostVisible from 'View/host-stream/hooks/useIsHelpHostVisible';
import { useHostStreamState } from 'View/host-stream/state';

import styles from './styles.module.css';

const LiveControl = ({ startTime, note, streamNumber }) => {
  const { t } = useTranslation('host-stream');
  const navigate = useNavigate();
  const { updateState } = useHostStreamState();
  const { name } = useUserInfo();

  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const scrollContainerRef = useRef(null);

  const isHelpHostVisible = useIsHelpHostVisible();

  const handleScroll = (e) => {
    setShowScrollToTop(e.target.scrollTop > 42);
  };

  const handleScrollToTop = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <Box className={styles['stream-control__container']}>
      <HeaderMobile type="header-dropdown" onClick={() => navigate('/host')} />
      <Flex ref={scrollContainerRef} onScroll={handleScroll} width="100%" flexDir="column" flex={1} overflowY="auto">
        <Box className={styles['stream-control__header--container']}>
          <LivestreamHeader isLivestreaming streamNumber={streamNumber} isControlView />
        </Box>
        <Box className={styles['stream-control__content']} marginBottom={isHelpHostVisible ? '0px' : '20px'}>
          <Flex marginBottom="12px">
            <Box flex={1} className={styles['stream-control__item']}>
              <TextRegular fontSize="var(--font-caption1)">{t('stream_control.host')}</TextRegular>
              <TextMedium fontSize="var(--font-body3)">{name}</TextMedium>
            </Box>
            <Box flex={1} className={styles['stream-control__item']}>
              <TextRegular fontSize="var(--font-caption1)">{t('stream_control.start_live')}</TextRegular>
              <Box display="flex" alignItems="center" gap="6px">
                <AlarmClockIcon color="var(--color-neutral50)" size={16} />
                <TextMedium fontSize="var(--font-body3)">
                  {moment(startTime).format('HH:mm')} <span className={styles['stream-control__date']}>{moment(startTime).format('DD/MM/YY')}</span>
                </TextMedium>
              </Box>
            </Box>
          </Flex>
          <Box className={styles['stream-control__item']}>
            <TextRegular fontSize="var(--font-caption1)">{t('stream_control.notes')}</TextRegular>
            <Box display="flex" alignItems="start" gap="6px">
              <PaperclipIcon color="var(--color-neutral50)" size={16} />
              <TextMedium className={styles['stream-control__note']} fontSize="var(--font-body3)">
                {note || '-'}
              </TextMedium>
            </Box>
          </Box>
        </Box>
        <Box className={styles['stream-control__help-host--container']}>
          <HelpHostWidget />
        </Box>
        <BriefLiveWidget />
      </Flex>
      <Box className={styles['stream-control__actions']}>
        <ButtonOutline className={styles['stream-control__button']} onClick={() => navigate('/host/stock-promo')}>
          <PromoTagIcon size={18} />
          {t('stream_control.view_stock')}
        </ButtonOutline>
        <ButtonSolid
          className={styles['stream-control__button']}
          background="var(--color-error50)"
          onClick={() => {
            updateState({ selectedView: 'record', currentStep: 0 });
          }}
        >
          <StopIcon size={16} />
          {t('stream_control.end_live')}
        </ButtonSolid>
      </Box>
      <ScrollToTopUiKit show={showScrollToTop} onClick={handleScrollToTop} bottom="160px" />
    </Box>
  );
};

export default LiveControl;
